import React, { useState } from 'react';
import type { AIAgentWebhook } from '@troia-v3/types';

import { getApiUrl } from '@/lib/config';
import { CardActionsMenu, DeleteConfirmationModal } from '@/shared/components/ui';
import type { CardAction } from '@/shared/components/ui';
import { Icon } from '@/shared/icons'
import { SectionCard } from './SectionCard';;

import { useAgent } from '../hooks';
import { useDeleteAgentWebhook, useUpdateAgentWebhookStatus } from '../hooks/useAgentWebhooks';

import { WebhookFormDrawer } from './WebhookFormDrawer';
import { WebhookLogsModal } from './WebhookLogsModal';

interface WebhooksTabProps {
  /** Sem id, o agente ainda não foi salvo — a aba fica em modo somente-leitura com aviso */
  agentId?: string;
}

const METHOD_COLORS: Record<string, { bg: string; text: string }> = {
  POST: { bg: 'rgba(52, 211, 153, 0.15)', text: '#10b981' },
  PUT: { bg: 'rgba(96, 165, 250, 0.15)', text: '#3b82f6' },
  PATCH: { bg: 'rgba(251, 191, 36, 0.15)', text: '#f59e0b' },
};

export const WebhooksTab: React.FC<WebhooksTabProps> = ({ agentId }) => {
  const { data: agent } = useAgent(agentId);
  const webhooks: AIAgentWebhook[] = agent?.webhooks || [];

  const [drawerState, setDrawerState] = useState<{ mode: 'create' | 'edit'; webhook: AIAgentWebhook | null } | null>(null);
  const [statusConfirmWebhook, setStatusConfirmWebhook] = useState<AIAgentWebhook | null>(null);
  const [deleteConfirmWebhook, setDeleteConfirmWebhook] = useState<AIAgentWebhook | null>(null);
  const [logsWebhook, setLogsWebhook] = useState<AIAgentWebhook | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const updateStatus = useUpdateAgentWebhookStatus(agentId);
  const deleteWebhook = useDeleteAgentWebhook(agentId);

  const getWebhookUrl = (webhook: AIAgentWebhook): string => {
    const base = getApiUrl();
    return `${base}/api/webhooks/agents/${webhook.id}/:conversationId`;
  };

  const handleCopyUrl = async (webhook: AIAgentWebhook): Promise<void> => {
    const url = getWebhookUrl(webhook);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(webhook.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // Fallback silencioso
    }
  };

  const handleConfirmStatusToggle = async (): Promise<void> => {
    if (!statusConfirmWebhook) return;
    const nextStatus = statusConfirmWebhook.status === 'active' ? 'inactive' : 'active';
    try {
      await updateStatus.mutateAsync({ webhookId: statusConfirmWebhook.id, status: nextStatus });
      setStatusConfirmWebhook(null);
    } catch {
      // Toast de erro já é emitido pelo hook (onError); modal permanece aberto
    }
  };

  const handleConfirmDelete = async (): Promise<void> => {
    if (!deleteConfirmWebhook) return;
    try {
      await deleteWebhook.mutateAsync(deleteConfirmWebhook.id);
      setDeleteConfirmWebhook(null);
    } catch {
      // Toast de erro já é emitido pelo hook (onError); modal permanece aberto
    }
  };

  return (
    <div className="space-y-4" data-testid="webhooks-tab">
      {/* Info Box */}
      <div
        className="p-4 rounded-lg border"
        style={{ backgroundColor: 'var(--theme-info-background)', borderColor: 'var(--theme-info-color)' }}
      >
        <p className="text-sm" style={{ color: 'var(--theme-info-color)' }}>
          <strong>Como funciona:</strong> Webhooks permitem que sistemas externos enviem dados JSON para uma URL.
          Os dados recebidos serão processados pela IA usando o prompt configurado, e o resultado será injetado
          como mensagem na conversa, estimulando o agente a responder.
        </p>
      </div>

      {!agentId && (
        <div
          className="mb-3 px-3 py-2 rounded-lg text-xs flex items-center gap-2"
          style={{ backgroundColor: 'var(--theme-warning-background)', color: 'var(--theme-warning-color)' }}
        >
          <Icon name="alert-circle" className="w-4 h-4 flex-shrink-0" />
          <span>Salve o agente primeiro para configurar webhooks.</span>
        </div>
      )}

      {webhooks.length > 0 && (
        <SectionCard
          icon="link"
          title="Webhooks Configurados"
          subtitle="URLs que recebem JSON externo e viram mensagem processada pela IA"
        >
          <div className="space-y-3">
          {webhooks.map((webhook) => {
            const url = getWebhookUrl(webhook);
            const methodColor = METHOD_COLORS[webhook.method] || METHOD_COLORS.POST;
            // Fallback legado: webhooks antigos têm prompt na raiz (IA sempre ativa)
            const aiConfig = webhook.aiProcessing
              ?? (webhook.prompt ? { enabled: true, prompt: webhook.prompt } : { enabled: false, prompt: '' });
            const filterCount = webhook.filters?.length ?? 0;

            const actions: CardAction[] = [
              { label: 'Editar', icon: 'edit', onClick: () => setDrawerState({ mode: 'edit', webhook }) },
              {
                label: webhook.status === 'active' ? 'Desativar' : 'Ativar',
                icon: 'alert-circle',
                onClick: () => setStatusConfirmWebhook(webhook),
              },
              { label: 'Log de webhooks', icon: 'list', onClick: () => setLogsWebhook(webhook) },
              { label: 'Excluir', icon: 'trash', variant: 'danger', onClick: () => setDeleteConfirmWebhook(webhook) },
            ];

            return (
              <div
                key={webhook.id}
                className="p-4 rounded-lg border"
                style={{
                  backgroundColor: 'var(--theme-card-background)',
                  borderColor: webhook.status === 'active' ? 'var(--theme-card-border)' : 'var(--theme-warning-color)',
                  opacity: webhook.status === 'inactive' ? 0.7 : 1,
                }}
                data-testid={`webhook-card-${webhook.id}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span
                      className="px-2 py-0.5 rounded text-xs font-bold"
                      style={{ backgroundColor: methodColor.bg, color: methodColor.text }}
                    >
                      {webhook.method}
                    </span>
                    <span className="text-sm font-medium" style={{ color: 'var(--theme-card-text)' }}>
                      {webhook.name}
                    </span>
                    <span
                      className="px-2 py-0.5 rounded-full text-xs"
                      style={{
                        backgroundColor: webhook.status === 'active' ? 'var(--theme-success-background)' : 'var(--theme-warning-background)',
                        color: webhook.status === 'active' ? 'var(--theme-success-color)' : 'var(--theme-warning-color)',
                      }}
                    >
                      {webhook.status === 'active' ? 'Ativo' : 'Inativo'}
                    </span>
                    {aiConfig.enabled && (
                      <span
                        className="px-2 py-0.5 rounded-full text-xs flex items-center gap-1"
                        style={{ backgroundColor: 'var(--theme-success-background)', color: 'var(--theme-success-color)' }}
                        title="Processamento com IA ativo"
                      >
                        <Icon name="cpu" className="w-3 h-3" />
                        IA
                      </span>
                    )}
                    {filterCount > 0 && (
                      <span
                        className="px-2 py-0.5 rounded-full text-xs flex items-center gap-1"
                        style={{ backgroundColor: 'var(--theme-info-background)', color: 'var(--theme-info-color)' }}
                        title={`${filterCount} filtro(s) configurado(s)`}
                      >
                        <Icon name="filter" className="w-3 h-3" />
                        {filterCount}
                      </span>
                    )}
                  </div>
                  <CardActionsMenu actions={actions} size="sm" data-testid={`webhook-actions-${webhook.id}`} />
                </div>

                {webhook.description && (
                  <p className="text-xs mb-3" style={{ color: 'var(--theme-muted-text)' }}>
                    {webhook.description}
                  </p>
                )}

                <div className="mb-3">
                  <label className="block text-xs mb-1" style={{ color: 'var(--theme-muted-text)' }}>
                    URL do Webhook
                  </label>
                  <div className="flex items-center gap-2">
                    <div
                      className="flex-1 px-3 py-2 rounded-lg text-xs font-mono truncate"
                      style={{
                        backgroundColor: 'var(--theme-input-background)',
                        border: '1px solid var(--theme-input-border)',
                        color: 'var(--theme-input-text)',
                      }}
                      title={url}
                    >
                      {url}
                    </div>
                    <button
                      onClick={() => handleCopyUrl(webhook)}
                      className="flex-shrink-0 p-2 rounded-lg transition-colors"
                      style={{
                        backgroundColor: copiedId === webhook.id ? 'var(--theme-success-background)' : 'var(--theme-button-ghost-hover)',
                        color: copiedId === webhook.id ? 'var(--theme-success-color)' : 'var(--theme-muted-text)',
                      }}
                      title="Copiar URL"
                      data-testid={`webhook-copy-url-${webhook.id}`}
                    >
                      {copiedId === webhook.id ? (
                        <Icon name="check" className="w-4 h-4" />
                      ) : (
                        <Icon name="copy" className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs mt-1" style={{ color: 'var(--theme-muted-text)' }}>
                    Substitua <code style={{ color: 'var(--theme-primary)' }}>:conversationId</code> pelo ID da conversa alvo.
                  </p>
                </div>

                <div>
                  <label className="block text-xs mb-1" style={{ color: 'var(--theme-muted-text)' }}>
                    Prompt de IA
                  </label>
                  <div
                    className="px-3 py-2 rounded-lg text-xs max-h-20 overflow-y-auto"
                    style={{
                      backgroundColor: 'var(--theme-input-background)',
                      border: '1px solid var(--theme-input-border)',
                      color: aiConfig.enabled ? 'var(--theme-card-text)' : 'var(--theme-muted-text)',
                    }}
                  >
                    {aiConfig.enabled ? aiConfig.prompt : 'Processamento com IA desativado — o JSON recebido é injetado como recebido.'}
                  </div>
                </div>
              </div>
            );
          })}
          </div>
        </SectionCard>
      )}

      {webhooks.length === 0 && agentId && (
        <div className="p-8 text-center rounded-lg border-2 border-dashed" style={{ borderColor: 'var(--theme-card-border)' }}>
          <Icon name="link" className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--theme-muted-text)', opacity: 0.4 }} />
          <p className="text-sm font-medium mb-1" style={{ color: 'var(--theme-card-text)' }}>
            Nenhum webhook configurado
          </p>
          <p className="text-xs mb-4" style={{ color: 'var(--theme-muted-text)' }}>
            Configure webhooks para receber dados de sistemas externos e processá-los com IA.
          </p>
          <button
            onClick={() => setDrawerState({ mode: 'create', webhook: null })}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white"
            style={{ backgroundColor: 'var(--theme-primary)' }}
            data-testid="webhook-add-empty-state"
          >
            <Icon name="plus" className="w-4 h-4" />
            Adicionar Webhook
          </button>
        </div>
      )}

      {webhooks.length > 0 && webhooks.length < 10 && (
        <button
          onClick={() => setDrawerState({ mode: 'create', webhook: null })}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed text-sm font-medium transition-colors"
          style={{ borderColor: 'var(--theme-card-border)', color: 'var(--theme-muted-text)' }}
          data-testid="webhook-add-button"
        >
          <Icon name="plus" className="w-4 h-4" />
          Adicionar Webhook ({webhooks.length}/10)
        </button>
      )}

      {!agentId && (
        <button
          disabled
          title="Salve o agente primeiro"
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed text-sm font-medium opacity-50 cursor-not-allowed"
          style={{ borderColor: 'var(--theme-card-border)', color: 'var(--theme-muted-text)' }}
          data-testid="webhook-add-disabled"
        >
          <Icon name="plus" className="w-4 h-4" />
          Novo Webhook
        </button>
      )}

      {webhooks.length > 0 && (
        <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
          <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
            <strong style={{ color: 'var(--theme-card-text)' }}>{webhooks.length}</strong> webhook(s) configurado(s)
            {webhooks.filter((w) => w.status === 'active').length !== webhooks.length && (
              <span> ({webhooks.filter((w) => w.status === 'active').length} ativo(s))</span>
            )}
          </p>
        </div>
      )}

      {agentId && drawerState && (
        <WebhookFormDrawer
          isOpen={!!drawerState}
          onClose={() => setDrawerState(null)}
          mode={drawerState.mode}
          agentId={agentId}
          webhook={drawerState.webhook}
        />
      )}

      {statusConfirmWebhook && (
        <DeleteConfirmationModal
          isOpen={!!statusConfirmWebhook}
          onClose={() => setStatusConfirmWebhook(null)}
          onConfirm={handleConfirmStatusToggle}
          variant="default"
          title={statusConfirmWebhook.status === 'active' ? 'Desativar Webhook' : 'Ativar Webhook'}
          message={
            statusConfirmWebhook.status === 'active'
              ? `Tem certeza que deseja desativar o webhook "${statusConfirmWebhook.name}"? Ele deixará de aceitar requisições.`
              : `Tem certeza que deseja ativar o webhook "${statusConfirmWebhook.name}"?`
          }
          confirmButtonText={statusConfirmWebhook.status === 'active' ? 'Desativar' : 'Ativar'}
          loadingText={statusConfirmWebhook.status === 'active' ? 'Desativando...' : 'Ativando...'}
          isLoading={updateStatus.isPending}
        />
      )}

      {deleteConfirmWebhook && (
        <DeleteConfirmationModal
          isOpen={!!deleteConfirmWebhook}
          onClose={() => setDeleteConfirmWebhook(null)}
          onConfirm={handleConfirmDelete}
          title="Excluir Webhook"
          message={`Tem certeza que deseja excluir o webhook "${deleteConfirmWebhook.name}"? Essa ação não pode ser desfeita.`}
          isLoading={deleteWebhook.isPending}
        />
      )}

      {logsWebhook && (
        <WebhookLogsModal
          isOpen={!!logsWebhook}
          onClose={() => setLogsWebhook(null)}
          webhook={logsWebhook}
        />
      )}
    </div>
  );
};

WebhooksTab.displayName = 'WebhooksTab';
